/* eslint-disable react-refresh/only-export-components */
/**
 * HelpContext — possède l'UNIQUE `useHelp()` et l'API passerelle du Help Drawer
 * (HELP-DRAWER §8). Créé par CE lot ; consommé par SEARCH-002 (palette ⌘K) plus
 * tard via `useHelpBridge()`.
 *
 * Deux contextes distincts :
 *   - `HelpStateContext` — état complet du drawer (+ slot consume-once) pour le
 *     drawer et le FAB ;
 *   - `HelpBridgeContext` — uniquement les deux ouvertures (identité STABLE), pour
 *     les consommateurs externes (palette) sans les faire re-rendre à chaque
 *     changement d'état du drawer.
 */

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react'
import { useHelp, type UseHelpReturn } from '@/hooks/useHelp'

export type HelpIntentKind = 'assistant' | 'articles'

/** Intention déposée par la passerelle, consommée UNE fois (clé = nonce). */
export interface PendingHelpIntent {
    text: string
    kind: HelpIntentKind
    /** Compteur monotone : clé de re-déclenchement (pas de re-fire à la réouverture). */
    nonce: number
}

interface HelpStateValue extends UseHelpReturn {
    pendingIntent: PendingHelpIntent | null
    /** Vide le slot après consommation (évite tout re-fire). */
    consumePendingIntent: () => void
}

export interface HelpBridgeValue {
    /** Ouvre le drawer et lance directement une conversation IA avec `text`. */
    openWithAssistantQuestion: (text: string) => void
    /** Ouvre le drawer et pré-remplit le champ unifié avec `text` (recherche locale). */
    openWithHelpSearch: (text: string) => void
    /** Ouvre simplement le drawer (champ unifié focalisé) — sans intention pré-remplie. */
    openHelp: () => void
}

const HelpStateContext = createContext<HelpStateValue | null>(null)
const HelpBridgeContext = createContext<HelpBridgeValue | null>(null)

export function HelpProvider({
    contexts,
    children,
}: {
    contexts: string[]
    children: ReactNode
}) {
    const help = useHelp({ contexts })
    const [pendingIntent, setPendingIntent] = useState<PendingHelpIntent | null>(null)
    const nonceRef = useRef(0)
    const { open } = help

    const dispatchIntent = useCallback(
        (text: string, kind: HelpIntentKind) => {
            const trimmed = text.trim()
            if (!trimmed) return
            nonceRef.current += 1
            open()
            setPendingIntent({ text: trimmed, kind, nonce: nonceRef.current })
        },
        [open],
    )

    const openWithAssistantQuestion = useCallback(
        (text: string) => dispatchIntent(text, 'assistant'),
        [dispatchIntent],
    )
    const openWithHelpSearch = useCallback(
        (text: string) => dispatchIntent(text, 'articles'),
        [dispatchIntent],
    )
    // Ouvre le drawer sans intention (champ unifié prêt à la saisie). Utilisé par
    // les passerelles de la palette quand la requête est VIDE (sinon dispatchIntent
    // ignore un texte vide → le drawer ne s'ouvrirait jamais).
    const openHelp = useCallback(() => open(), [open])
    const consumePendingIntent = useCallback(() => setPendingIntent(null), [])

    const stateValue = useMemo<HelpStateValue>(
        () => ({ ...help, pendingIntent, consumePendingIntent }),
        [help, pendingIntent, consumePendingIntent],
    )
    // Identité STABLE : la palette ne re-rend pas quand l'état du drawer change.
    const bridgeValue = useMemo<HelpBridgeValue>(
        () => ({ openWithAssistantQuestion, openWithHelpSearch, openHelp }),
        [openWithAssistantQuestion, openWithHelpSearch, openHelp],
    )

    return (
        <HelpBridgeContext.Provider value={bridgeValue}>
            <HelpStateContext.Provider value={stateValue}>{children}</HelpStateContext.Provider>
        </HelpBridgeContext.Provider>
    )
}

/** État complet du drawer (drawer + FAB). À utiliser sous `HelpProvider`. */
export function useHelpContext(): HelpStateValue {
    const ctx = useContext(HelpStateContext)
    if (!ctx) throw new Error('useHelpContext must be used within a HelpProvider')
    return ctx
}

/**
 * API passerelle STABLE pour ouvrir le drawer depuis l'extérieur (palette ⌘K).
 * Ne provoque pas de re-render au gré de l'état du drawer.
 */
export function useHelpBridge(): HelpBridgeValue {
    const ctx = useContext(HelpBridgeContext)
    if (!ctx) throw new Error('useHelpBridge must be used within a HelpProvider')
    return ctx
}
